<template>
  <div class="profile-panel">
    <el-card class="section-card profile-card" shadow="never">
      <div class="section-head">
        <div>
          <p class="eyebrow">Customer Profile</p>
          <h2>Profile details</h2>
          <p class="subcopy">These are the details saved from your customer registration form.</p>
        </div>
        <el-button type="primary" @click="$emit('edit-profile')">
          Edit details
        </el-button>
      </div>

      <el-alert
        v-if="!profileCompleted"
        type="warning"
        :closable="false"
        title="Your profile is incomplete. Finish the form to unlock submission creation."
        class="profile-alert"
      />

      <div class="profile-grid">
        <el-card class="detail-card" shadow="never">
          <h3>Personal information</h3>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="First name">{{ profile.firstName || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Last name">{{ profile.lastName || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Email">{{ profile.email || userEmail || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Primary contact">{{ profile.primaryContactNumber || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="International student">{{ yesNoLabel(profile.isInternationalStudent) }}</el-descriptions-item>
            <el-descriptions-item label="Country of citizenship">{{ profile.countryOfCitizenship || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Current status">{{ currentStatusLabel }}</el-descriptions-item>
            <el-descriptions-item label="Highest education">{{ educationLabel }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card class="detail-card" shadow="never">
          <h3>Service preferences</h3>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="Requested services">{{ requestedServicesLabel }}</el-descriptions-item>
            <el-descriptions-item label="Preferred schedule">{{ scheduleLabel }}</el-descriptions-item>
            <el-descriptions-item label="Service time frame">{{ timeFrameLabel }}</el-descriptions-item>
            <el-descriptions-item label="Special requirements">{{ profile.specialRequirementsOrPreferences || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Registration decision">{{ registrationDecisionLabel }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
      </div>
    </el-card>

    <el-card class="section-card password-card" shadow="never">
      <ChangePassword />
    </el-card>
  </div>
</template>

<script>
import ChangePassword from '@/components/ChangePassword.vue'

const STATUS_LABELS = Object.freeze({
  student: 'Student',
  employed: 'Employed',
  unemployed: 'Unemployed',
  retired: 'Retired',
  prefer_not_to_say: 'Prefer not to say',
  other: 'Other',
})

const EDUCATION_LABELS = Object.freeze({
  elementary: 'Elementary',
  middle_school: 'Middle school',
  high_school: 'High school',
  college_certificate: 'College certificate',
  university_degree: 'University degree',
  undeclared: 'Undeclared',
  prefer_not_to_say: 'Prefer not to say',
  other: 'Other',
})

const SCHEDULE_LABELS = Object.freeze({
  monday_to_friday_morning: 'Monday to Friday - Morning',
  monday_to_friday_afternoon: 'Monday to Friday - Afternoon',
  monday_to_friday_evening: 'Monday to Friday - Evening',
})

const TIME_FRAME_LABELS = Object.freeze({
  standard_service: 'Standard service (2-6 weeks)',
  expedited_service: 'Expedited service (1-2 weeks)',
})

const REGISTRATION_LABELS = Object.freeze({
  agree: 'Agreed',
  disagree: 'Disagreed',
})

export default {
  name: 'CustomerProfilePanel',
  components: { ChangePassword },
  props: {
    profile: {
      type: Object,
      default: () => ({}),
    },
    profileCompleted: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      default: '',
    },
  },
  emits: ['edit-profile'],
  computed: {
    currentStatusLabel() {
      if (this.profile.currentStatus === 'other' && this.profile.currentStatusOther) {
        return this.profile.currentStatusOther
      }
      return STATUS_LABELS[this.profile.currentStatus] || 'Not provided'
    },
    educationLabel() {
      if (this.profile.highestEducation === 'other' && this.profile.highestEducationOther) {
        return this.profile.highestEducationOther
      }
      return EDUCATION_LABELS[this.profile.highestEducation] || 'Not provided'
    },
    requestedServicesLabel() {
      return Array.isArray(this.profile.requestedServices) && this.profile.requestedServices.length
        ? this.profile.requestedServices.join(', ')
        : 'Not provided'
    },
    scheduleLabel() {
      return SCHEDULE_LABELS[this.profile.preferredSchedule] || 'Not provided'
    },
    timeFrameLabel() {
      return TIME_FRAME_LABELS[this.profile.serviceTimeFrame] || 'Not provided'
    },
    registrationDecisionLabel() {
      return REGISTRATION_LABELS[this.profile.registrationDecision] || 'Not provided'
    },
  },
  methods: {
    yesNoLabel(value) {
      if (value === 'yes') return 'Yes'
      if (value === 'no') return 'No'
      return 'Not provided'
    },
  },
}
</script>

<style scoped>
.profile-panel {
  display: grid;
  gap: 18px;
}

.profile-card,
.password-card {
  border-radius: 22px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.section-head.compact {
  margin-bottom: 0;
}

.eyebrow {
  margin: 0 0 6px;
  color: #0b6cff;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-head h2 {
  margin: 0 0 6px;
  color: #03241f;
  font-size: 1.5rem;
  font-weight: 850;
}

.subcopy {
  margin: 0;
  color: #4f6d67;
}

.profile-alert {
  margin-bottom: 16px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.detail-card {
  border-radius: 18px;
  border: 1px solid rgba(3, 36, 31, 0.08);
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.detail-card h3 {
  margin: 0 0 14px;
  color: #003831;
  font-size: 1.1rem;
  font-weight: 800;
}

.detail-card :deep(.el-descriptions__label) {
  width: 180px;
  color: #003831;
  font-weight: 700;
  background: rgba(0, 56, 49, 0.05);
}

.detail-card :deep(.el-descriptions__content) {
  color: #1f2a25;
  white-space: normal;
  overflow-wrap: anywhere;
}

.password-card :deep(.page) {
  padding: 0;
  justify-content: stretch;
}

.password-card :deep(.card) {
  width: 100%;
  border: none;
  box-shadow: none;
  padding: 0;
}

@media (max-width: 860px) {
  .profile-grid {
    grid-template-columns: 1fr;
  }

  .section-head {
    flex-direction: column;
  }
}
</style>
